import {
  formatMinutesToHoursAndMinutes,
  minutesToHoursAndMinutes
} from 'utils/hours'
import { IHealthDataInfo } from './types'
import { decimalToString } from 'utils/decimal'
import { calculateStepsMean } from '../utils/calculate_steps_mean_data'
import {
  COMMON_MESSAGES,
  COMMON_REFERENCE_AREAS,
  createRiskProtocol
} from './health_data_constants'

export const healthDataContent: IHealthDataInfo = {
  // CALCULATIONS
  overall_calculation: {
    title: 'Pontuação Geral da Saúde',
    dataOptions: {
      type: '%'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A Pontuação Geral de Saúde, baseada no National Early Warning Score (NEWS) 2, monitora seis sinais vitais — frequência respiratória, saturação de oxigênio, uso de oxigênio suplementar, temperatura corporal, pressão arterial sistólica e frequência cardíaca. Ela ajuda a identificar sinais precoces de piora da saúde, permitindo ações rápidas para prevenir complicações graves, como parada cardíaca ou risco de sepse.<br /><br /><i>Fonte: Royal College of Physicians. National Early Warning Score (NEWS) 2: Padronizando a avaliação da gravidade de doenças agudas no NHS. Relatório atualizado de um grupo de trabalho. Londres: RCP, 2017. Para mais informações, consulte a versão original em inglês do relatório.</i>'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o cálculo da Pontuação Geral de Saúde, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Pressão arterial<br />\u2022 Frequência respiratória<br />\u2022 Saturação de oxigênio<br />\u2022 Frequência cardíaca<br />\u2022 Uso de oxigênio suplementar<br />\u2022 Temperatura Corporal'
      },
      {
        title: 'Protocolos',
        description:
          'De acordo com guidelines do NEWS2, segue pontuação de referência e respectiva recomendação para <b>Pontuação Geral de Saúde:</b><br /><br /><b>80 – 100: </b>Acompanhamento normal com médico;<br /><br /><b>70 – 79:</b> Limite crítico para intervenção urgente - resposta de uma equipe capacitada para tratar pacientes graves e escalar cuidados quando necessário; e <br /><br /><b>< 69:</b> Intervenção urgente ou de emergência - A equipe de resposta deve incluir profissionais com habilidades em cuidados críticos, como manejo das vias aéreas.<br /><br />Adicionalmente:<br /><br /><b>Pontuação < 75: </b>pacientes com infecção, sinais de infecção ou risco de infecção, avalie a possibilidade de sepse.<br /><br />Qualquer sinal vital, que isoladamente, subtraia <b>15 pontos</b> requer intervenção urgente - resposta de uma equipe capacitada para checar a veracidade dos dados, tratar pacientes graves e escalar cuidados quando necessário. '
      },
      {
        title: 'Aviso Importante',
        description:
          'Os dados calculados ou inseridos manualmente no app, incluindo aqueles provenientes do seu relógio, não devem ser usados de forma isolada para diagnósticos ou recomendações médicas. Sempre consulte seu médico ou profissional de saúde para uma avaliação completa e orientações adequadas.'
      }
    ],
    references: [
      {
        title:
          'National Early Warning Score (NEWS) 2: Standardising the assessment of acute-illness severity in the NHS. ',
        link: 'https://www.rcp.ac.uk/media/a4ibkkbf/news2-final-report_0_0.pdf'
      },
      {
        title:
          'The ability of the National Early Warning Score (NEWS) to discriminate patients at risk of early cardiac arrest, unanticipated intensive care unit admission, and death',
        link: 'https://pubmed.ncbi.nlm.nih.gov/23295778/'
      },
      {
        title:
          'ViEWS Towards a national Early Warning Score for detecting adult inpatient deterioration',
        link: 'https://pubmed.ncbi.nlm.nih.gov/20637974/'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      referenceAreas: [
        {
          y1: 0,
          y2: 70,
          fill: 'red'
        },
        {
          y1: 70,
          y2: 80,
          fill: 'orange'
        },
        {
          y1: 80,
          y2: 95,
          fill: 'grey'
        },
        {
          y1: 95,
          y2: 100,
          fill: 'green'
        }
      ]
    }
  },
  stroke_calculation: {
    title: 'Risco de AVC em 10 anos',
    dataOptions: {
      type: '%'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A identificação precoce do risco de AVC é essencial para prevenção e tratamento eficazes. O cálculo permite personalizar tratamentos e orientar mudanças no estilo de vida, como controle da pressão arterial e dieta saudável.'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de AVC, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Sexo biológico<br />\u2022 Idade<br />\u2022 Pressão arterial<br />\u2022 Se tem diabetes<br />\u2022 Se é fumante<br />\u2022 Se já possui doença cardiovascular<br />\u2022 Se já tem fibrilação atrial<br />\u2022 Se possui hipertrofia ventricular esquerda<br />\u2022 Se toma medicação para hipertensão'
      },
      {
        title: 'Protocolos',
        description: createRiskProtocol()
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title:
          'Stroke risk profile: adjustment for antihypertensive medication. The Framingham Study',
        link: 'https://pubmed.ncbi.nlm.nih.gov/8266381/'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      hidePeriod: ['day', 'week'],
      referenceAreas: COMMON_REFERENCE_AREAS.RISK_ZONES
    }
  },
  cvd_calculation: {
    title: 'Risco de Doença Cardiovascular em 10 anos',
    dataOptions: {
      type: '%'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'O cálculo de risco cardiovascular permite intervenções preventivas eficazes, ajudando a reduzir eventos como infartos e insuficiência cardíaca. Ele orienta tratamentos personalizados e incentiva hábitos saudáveis, como dieta balanceada, exercícios e controle de fatores de risco. A análise integrada dos dados de saúde garante uma abordagem abrangente e precisa.'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de Doença Cardio Vascular, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Sexo biológico<br />\u2022 Idade<br />\u2022 Pressão arterial<br />\u2022 Se é fumante<br />\u2022 Se tem diabetes<br />\u2022 Índice de Massa Corporal (IMC)<br />\u2022 Se toma medicação para hipertensão'
      },
      {
        title: 'Protocolos',
        description: createRiskProtocol()
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title:
          'General cardiovascular risk profile for use in primary care: the Framingham Heart Study',
        link: 'https://pubmed.ncbi.nlm.nih.gov/18212285/'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      hidePeriod: ['day', 'week'],
      referenceAreas: COMMON_REFERENCE_AREAS.RISK_ZONES
    }
  },
  hypertension_calculation: {
    title: 'Risco de Hipertensão em 4 anos',
    dataOptions: {
      type: '%'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A avaliação precoce do cálculo de hipertensão ajuda a prevenir complicações como doenças cardiovasculares e AVC. Ele orienta mudanças de estilo de vida, controle de peso, exercícios e monitoramento da pressão arterial. A detecção precoce permite tratamentos personalizados e melhora a qualidade de vida, com análise integrada dos dados de saúde para maior precisão.'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de Hipertensão, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Sexo biológico<br />\u2022 Idade<br />\u2022 Pressão arterial<br />\u2022 Histórico de hipertensão na familia<br />\u2022 Se é fumante'
      },
      {
        title: 'Protocolos',
        description: createRiskProtocol()
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title:
          'A risk score for predicting near-term incidence of hypertension: the Framingham Heart Study',
        link: 'https://pubmed.ncbi.nlm.nih.gov/18195335/'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      hidePeriod: ['day', 'week'],
      referenceAreas: COMMON_REFERENCE_AREAS.RISK_ZONES
    }
  },
  atrial_fibrillation_calculation: {
    title: 'Risco de Fibrilação Atrial em 10 anos',
    dataOptions: {
      type: '%'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'O cálculo de fibrilação atrial é crucial para prevenir derrames e manter a saúde cardíaca. Sua detecção precoce permite tratamentos personalizados e manejo eficaz dos sintomas, melhorando a qualidade de vida. Também incentiva mudanças no estilo de vida que reduzem riscos e promovem bem-estar.'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de Fibrilação Atrial, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Sexo biológico<br />\u2022 Idade<br />\u2022 Índice de Massa Corporal (IMC)<br />\u2022 Pressão arterial<br />\u2022 Tratamento de hipertensão<br />\u2022 Intervalo de PR<br />\u2022 Existência de Sopro Cardíaco<br />\u2022 Caso de insuficiência cardíaca'
      },
      {
        title: 'Protocolos',
        description: createRiskProtocol()
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title:
          'Development of a risk score for atrial fibrillation (Framingham Heart Study): A community-based cohort study',
        link: 'https://pubmed.ncbi.nlm.nih.gov/19249635/'
      },
      {
        title:
          'Echocardiographic predictors of nonrheumatic atrial fibrillation: the Framingham Heart Study',
        link: 'https://pubmed.ncbi.nlm.nih.gov/8313561/'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      hidePeriod: ['day', 'week'],
      referenceAreas: COMMON_REFERENCE_AREAS.RISK_ZONES
    }
  },
  sleep_score: {
    title: 'Pontuação de Sono',
    dataOptions: {},
    info: [
      {
        title: 'Descrição',
        description:
          'A Pontuação do Sono avalia a qualidade do sono integrando fatores como duração, proporção de sono profundo, REM e leve, além de despertares noturnos. Ela reflete como o sono contribui para a recuperação física e mental, impactando saúde cognitiva e emocional. O monitoramento contínuo ajuda a identificar padrões de comportamento e possíveis condições relacionadas ao sono, permitindo ajustes e manejo proativo para melhorar o bem-estar e saúde.<br /><br /><i>Fonte: National Institute of Health (EUA)</i>'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de Pontuação do Sono, os seguintes parâmetros devem ser fornecidos nas últimas 24horas:<br />\u2022 Total de minutos dormindo<br />\u2022 Total de minutos em sono profundo<br />\u2022 Total de minutos em sono REM.'
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title: 'How Sleep Works',
        link: 'https://www.nhlbi.nih.gov/health/sleep'
      },
      {
        title: 'Physiology, Sleep Stages',
        link: 'https://www.ncbi.nlm.nih.gov/books/NBK526132/'
      },
      {
        title: 'Your Guide to Health Sleep',
        link: 'https://www.nhlbi.nih.gov/files/docs/public/sleep/healthy_sleep.pdf'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      hidePeriod: ['day', 'week'],
      hasMaxAndMin: false,
      referenceAreas: COMMON_REFERENCE_AREAS.SLEEP_SCORE
    }
  },
  stress_level_calculation: {
    title: 'Estressores e Sobrecarga Fisiológica',
    dataOptions: {},
    info: [
      {
        title: 'Descrição',
        description:
          'A sobrecarga fisiológica reflete o impacto de estressores físicos e mentais, que podem ser internos (como doenças) ou externos (como trabalho e padrões de sono). Monitoramos esse nível com base na Variabilidade da Frequência Cardíaca (VFC), idade e gênero. Isso é crucial porque altos níveis de sobrecarga podem levar a problemas de saúde, como hipertensão, doenças cardíacas e imunidade enfraquecida.<br /><br /><i>Fonte: HRV demographics</i>'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de Estressores e sobrecarga fisiológica, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Sexo biológico<br />\u2022 Idade<br />\u2022 VFC'
      },
      {
        title: 'Protocolos',
        description:
          '<b>Abaixo de 50:</b> Predominância Parassimpática (Alta VFC), considerada benéfica e associada a melhor prognóstico cardiovascular.<br /><br /><b>Acima de 50:</b> Predominância Simpática (Baixa VFC), associada a uma ampla gama de estressores, como infecções (vírus), sobrecarga emocional, esforço físico intenso ou condições subjacentes como hipertensão e insuficiência cardíaca. Essa condição reflete o estado de alerta do organismo, podendo sinalizar desequilíbrios que exigem atenção para evitar complicações.'
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title: 'HRV Demographics, Part 1 - Age & Gender',
        link: 'https://elitehrv.com/hrv-demographics-age-gender'
      },
      {
        title: 'HRV Demographics, Part 2- Fitness Level',
        link: 'https://elitehrv.com/hrv-demographics-fitness-level'
      },
      {
        title:
          'Noções básicas de variabilidade da frequência cardíaca e sua aplicabilidade clínica',
        link: 'https://www.scielo.br/j/rbccv/a/Yh54M3tJK4tgWD5PSGcnmPK/'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      referenceAreas: COMMON_REFERENCE_AREAS.STRESS_LEVEL
    }
  },
  heart_age_calculation: {
    title: 'Idade Cardíaca/Vascular',
    dataOptions: {},
    info: [
      {
        title: 'Descrição',
        description:
          'A Idade Cardíaca/Vascular estima o envelhecimento cardiovascular comparado à idade cronológica, com base em fatores como pressão arterial, colesterol, hábitos de vida e histórico familiar, oferecendo uma visão clara do risco cardiovascular. Como muitos fatores são modificáveis, mudanças no estilo de vida e tratamentos podem melhorar a saúde do coração e promover longevidade.'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o Cálculo de Idade Cardíaca/Vascular, os seguintes parâmetros devem ser fornecidos nas últimas horas:<br />\u2022 Sexo biológico<br />\u2022 Idade<br />\u2022 Pressão arterial<br />\u2022 Se é fumante<br />\u2022 Se tem diabetes<br />\u2022 Índice de Massa Corporal (IMC)<br />\u2022 Se toma medicação para hipertensão'
      },
      {
        title: 'Protocolos',
        description:
          'Diferenças Relevantes: <br /><br />><b>10 anos:</b> Indicativo de alto risco cardiovascular, requer atenção médica imediata e intervenções intensivas.<br /><br /><b>5–10 anos:</b> Risco moderado; ações preventivas devem ser adotadas.<br /><br /><b><5 anos:</b> Pequena discrepância; pode ser monitorada sem intervenções emergenciais, mas mudanças no estilo de vida ainda são recomendadas.<br /><br /><b>Idade Cardíaca/Vascular = Idade cronológica:</b> Saúde cardiovascular alinhada com a idade; manter estilo de vida saudável e realizar check-ups regulares. <br /><br /><b>Idade Cardíaca/Vascular < Idade cronológica: </b> Risco cardiovascular baixo; sustentar hábitos saudáveis e monitorar periodicamente para evitar complacência.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Os dados calculados ou inseridos manualmente no app, incluindo aqueles provenientes do seu relógio, não devem ser usados de forma isolada para diagnósticos ou recomendações médicas. Sempre consulte seu médico ou profissional de saúde para uma avaliação completa e orientações adequadas.'
      }
    ],
    references: [
      {
        title:
          'General cardiovascular risk profile for use in primary care: the Framingham Heart Study',
        link: 'https://pubmed.ncbi.nlm.nih.gov/18212285/'
      }
    ],
    graphOptions: {
      hidePeriod: ['day', 'week'],
      type: 'lines-with-bar'
    }
  },
  bmi: {
    title: 'Índice de Massa Corporal (IMC)',
    info: [
      {
        title: 'Descrição',
        description:
          'O Índice de Massa Corporal (IMC) é uma medida útil para avaliar o risco de doenças relacionadas ao peso, como diabetes tipo 2, hipertensão e doenças cardiovasculares. No entanto, ele é uma ferramenta generalista e pode não ser preciso para todos os indivíduos. Por exemplo, pessoas muito musculosas podem ter um Índice de Massa Corporal (IMC) elevado sem apresentar excesso de gordura.'
      },
      {
        title: 'Parâmetros',
        description:
          'Para o cálculo do Índice de massa corporal (IMC), os seguintes parâmetros devem ser fornecidos nas últimas horas: <ul> <li>Peso</li> <li>Altura</li> </ul>'
      },
      {
        title: 'Protocolos',
        description:
          'As faixas de Índice de Massa Corporal (IMC) são iguais para homens e mulheres. Os valores padrões da Organização Mundial da Saúde (OMS) são:<br /><br /><b>Abaixo do peso (IMC < 18,5) - Fragilidade: Indica peso insuficiente.</b> Pode estar associado a deficiências nutricionais, baixa imunidade, fadiga e risco aumentado de problemas de saúde como osteoporose.<br /><br /><b>Peso normal (saudável) (IMC 18,5 – 24,9) - Saúde e equilibrio:</b> Faixa considerada ideal para a maioria das pessoas. Geralmente indica menor risco de problemas relacionados ao peso.<br /><br /><b>Sobrepeso (IMC 25 – 29,9) - Alerta moderado:</b> Excesso de peso moderado. Associado a um risco maior de desenvolver condições como hipertensão e diabetes tipo 2.<br /><br /><b>Obesidade Grau I (leve) (IMC 30 – 34,9) - Requer atenção:</b> Excesso de peso significativo. Risco aumentado de problemas como doenças cardíacas, diabetes tipo 2 e apneia do sono.<br /><br /><b>Obesidade Grau II (moderada) (IMC 35 – 39,9) - Risco significativo:</b> Obesidade mais grave. Maior risco de complicações graves, incluindo doenças metabólicas, articulares e cardiovasculares<br /><br /><b>Obesidade Grau III (grave ou mórbida) (IMC ≥ 40) - Alto risco, crítico:</b> Excesso de peso extremo. Altíssimo risco de doenças com risco de vida, como problemas cardíacos graves, acidente vascular cerebral, certos tipos de câncer e mobilidade severamente limitada.'
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.WARNING_DISCLAIMER
      }
    ],
    references: [
      {
        title: 'Body Mass Index, BMI Calculator, Healthy BMI',
        link: 'https://www.nhlbi.nih.gov/health/educational/lose_wt/bmitools.htm'
      },
      {
        title: 'Body mass index (BMI)',
        link: 'https://www.who.int/data/gho/data/themes/topics/topic-details/GHO/body-mass-index'
      }
    ],
    dataOptions: {
      formatValue: value => decimalToString(Number(value))
    },
    graphOptions: {
      type: 'lines-with-bar',
      hidePeriod: ['day', 'week'],
      formatValue: value => decimalToString(Number(value)),
      referenceAreas: COMMON_REFERENCE_AREAS.BMI
    }
  },
  // VITAL SIGNS
  /* blood_glucose_level: {
    title: 'Nivel de Glicose',
    dataOptions: {
      type: 'mg/dL'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'O monitoramento dos níveis de glicose é fundamental para diagnosticar e gerenciar o diabetes, ajustando tratamentos e prevenindo complicações metabólicas. Ele reflete o uso de energia do corpo, avalia o impacto da dieta e estilo de vida e identifica riscos precoces para intervenções preventivas.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['000'],
      placeholder: 'ex: 85',
      validate(value) {
        const blood_glucose_level = Number(value)
        if (
          Number.isNaN(blood_glucose_level) ||
          blood_glucose_level < 25 ||
          blood_glucose_level > 300
        ) {
          return 'Valores permitidos: entre 25 e 250'
        }
        return false
      },
      getJson(value) {
        const blood_glucose_level = Number(value)
        return {
          blood_glucose_level
        }
      }
    }
  }, */
  heart_rate: {
    title: 'Frequência Cardíaca',
    dataOptions: {
      type: 'bpm'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'O monitoramento contínuo da frequência cardíaca fornece uma visão em tempo real da saúde cardiovascular, ajudando a detectar condições cardíacas precoces e avaliando o condicionamento físico. Também é crucial para compreender a resposta ao estresse e a recuperação do corpo, auxiliando na personalização de cuidados e programas de saúde'
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.METRICS_DISCLAIMER
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['000'],
      placeholder: 'ex: 120',
      validate(value) {
        const heart_rate = Number(value)
        if (Number.isNaN(heart_rate) || heart_rate < 30 || heart_rate > 250) {
          return 'Valores permitidos: entre 30 e 250'
        }
        return false
      },
      getJson(value) {
        const heart_rate = Number(value)
        return {
          heart_rate
        }
      }
    }
  },
  heart_rate_rest: {
    title: 'Frequência Cardíaca em Repouso',
    dataOptions: {
      type: 'bpm'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A frequência cardíaca em repouso é um indicador vital da saúde cardiovascular e condicionamento físico, ajudando a avaliar riscos, monitorar recuperação e refletir estados emocionais ou níveis de glicose. Seu acompanhamento ao longo do tempo apoia tratamentos e mudanças de estilo de vida.'
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.METRICS_DISCLAIMER
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['000'],
      placeholder: 'ex: 120',
      validate(value) {
        const heart_rate_rest = Number(value)
        if (
          Number.isNaN(heart_rate_rest) ||
          heart_rate_rest < 30 ||
          heart_rate_rest > 250
        ) {
          return 'Valores permitidos: entre 30 e 250'
        }
        return false
      },
      getJson(value) {
        const heart_rate_rest = Number(value)
        return {
          heart_rate_rest
        }
      }
    }
  },
  hrv_sdnn: {
    title: 'VFC SDNN',
    dataOptions: {
      type: 'ms'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A VFC reflete a saúde do sistema nervoso autônomo e é um indicador importante da saúde cardíaca, recuperação e bem-estar geral. Ela auxilia na detecção precoce de problemas cardíacos, avaliação de estressores internos e externos e adaptações no condicionamento físico, sendo útil para prevenir doenças e personalizar cuidados.'
      },
      {
        title: 'Aviso Importante',
        description: COMMON_MESSAGES.METRICS_DISCLAIMER
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['000'],
      placeholder: 'ex: 120',
      validate(value) {
        const hrv_sdnn = Number(value)
        if (Number.isNaN(hrv_sdnn) || hrv_sdnn < 0 || hrv_sdnn > 200) {
          return 'Valor máximo permitido: 200'
        }
        return false
      },
      getJson(value) {
        const hrv_sdnn = Number(value)
        return {
          hrv_sdnn
        }
      }
    }
  },
  hrv_rmssd: {
    title: 'VFC rMSSD',
    dataOptions: {
      type: 'ms'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A VFC reflete a saúde do sistema nervoso autônomo e é um indicador importante da saúde cardíaca, recuperação e bem-estar geral. Ela auxilia na detecção precoce de problemas cardíacos, avaliação de estressores internos e externos e adaptações no condicionamento físico, sendo útil para prevenir doenças e personalizar cuidados.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['000'],
      placeholder: 'ex: 120',
      validate(value) {
        const hrv_rmssd = Number(value)
        if (Number.isNaN(hrv_rmssd) || hrv_rmssd < 0 || hrv_rmssd > 200) {
          return 'Valor máximo permitido: 200'
        }
        return false
      },
      getJson(value) {
        const hrv_rmssd = Number(value)
        return {
          hrv_rmssd
        }
      }
    }
  },
  o2_saturation: {
    title: 'Saturação de O2',
    dataOptions: {
      type: '%'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A saturação de oxigênio é essencial para avaliar a saúde respiratória e detectar hipóxia precocemente. Informa decisões sobre suporte respiratório e avalia a resposta à atividade física, especialmente em condições crônicas como DPOC (Doença Pulmonar Obstrutiva Crônica) e insuficiência cardíaca.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      yAxisDomain: [70, 100]
    },
    manualInputGuide: {
      mask: ['000'],
      placeholder: 'ex: 95',
      validate(value) {
        const o2_saturation = Number(value)
        if (
          Number.isNaN(o2_saturation) ||
          o2_saturation < 40 ||
          o2_saturation > 100
        ) {
          return 'Valores permitidos: entre 40 e 100'
        }
        return false
      },
      getJson(value) {
        const o2_saturation = Number(value)
        return {
          o2_saturation
        }
      }
    }
  },
  respiratory_rate: {
    title: 'Frequência Respiratória',
    dataOptions: {
      type: 'rpm'
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A frequência respiratória é um indicador chave da saúde respiratória, refletindo respostas a estressores internos e externos (e.g., infecções), condicionamento físico e possíveis condições subjacentes. Seu monitoramento auxilia na avaliação de tratamentos e no ajuste de terapias para cuidados personalizados.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['00'],
      placeholder: 'ex: 30',
      validate(value) {
        const respiratory_rate = Number(value)
        if (
          Number.isNaN(respiratory_rate) ||
          respiratory_rate < 1 ||
          respiratory_rate > 75
        ) {
          return 'Valores permitidos: entre 1 e 75'
        }
        return false
      },
      getJson(value) {
        const respiratory_rate = Number(value)
        return {
          respiratory_rate
        }
      }
    }
  },
  sleep_total: {
    title: 'Tempo de Sono',
    info: [
      {
        title: 'Descrição',
        description:
          'O Sono Total reúne todas as fases do ciclo de sono: leve, profundo e REM. Ele reflete a capacidade de descansar e restaurar corpo e mente. Dormir o suficiente é essencial para o equilíbrio físico, mental e emocional. Monitorar o sono total e sua qualidade garante tempo para reparação e recuperação, promovendo saúde e bem-estar.' +
          '<br/><h4>Sono REM</h4>' +
          'O sono REM é uma fase essencial para a consolidação de memórias, equilíbrio emocional e recuperação mental. Ele suporta a saúde cognitiva, aprendizagem e bem-estar psicológico. Monitorar sua qualidade ajuda a identificar problemas de memória, concentração e humor relacionados ao sono.' +
          '<br/><h4>Sono Leve</h4>' +
          'O sono leve é a fase de transição entre a vigília e o sono profundo, onde o corpo relaxa e a respiração e os batimentos cardíacos desaceleram. Ele prepara o corpo para fases mais restauradoras, regula a temperatura e facilita as transições no ciclo do sono.' +
          '<br/><h4>Sono Profundo</h4>' +
          'O sono profundo é a fase mais restauradora do sono, onde o corpo realiza a reparação muscular, a regeneração de tecidos e o fortalecimento do sistema imunológico. Durante o sono profundo, a atividade cerebral diminui significativamente, e o corpo se concentra na recuperação física. Essa fase é essencial para a regeneração celular e para manter a saúde física a longo prazo. A falta de sono profundo pode impactar negativamente a saúde geral, levando a fadiga, menor capacidade de recuperação e até mesmo enfraquecimento do sistema imunológico.' +
          '<br/><h4>Tempo Acordado</h4>' +
          'O tempo acordado durante a noite inclui despertares naturais ou causados por fatores externos. Embora breves interrupções sejam normais, frequentes podem afetar a qualidade do sono e causar cansaço. Monitorá-lo ajuda a identificar e reduzir fatores que prejudicam o descanso.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    dataOptions: {
      auxData: ['sleep_rem', 'sleep_light', 'sleep_deep', 'sleep_awake'],
      separator: '/',
      formatValue: value => formatMinutesToHoursAndMinutes(Number(value))
    },
    graphOptions: {
      type: 'stacked-bar',
      hidePeriod: ['day'],
      formatValue: value =>
        `${minutesToHoursAndMinutes(Number(value))?.hours ?? '0'} h`,

      useEndOfDay: true
    },
    manualInputGuide: {
      isTime: true,
      validate(value: string) {
        if (Number(value) > 999) {
          return 'Valor máximo permitido: 16h39'
        }
        return false
      },
      getJson(value, keySuffix) {
        const sleep = Number(value)
        return {
          [`sleep_${keySuffix ?? 'total'}`]: sleep
        }
      }
    }
  },
  sleep_rem: {
    title: 'Tempo de Sono REM'
  },
  sleep_light: {
    title: 'Tempo de Sono Leve'
  },
  sleep_deep: {
    title: 'Tempo de Sono Profundo'
  },
  sleep_awake: {
    title: 'Tempo Acordado'
  },
  steps: {
    title: 'Passos',
    dataOptions: {
      formatValue: value => decimalToString(Math.floor(Number(value))),
      summaryTypes: ['average', 'total'],
      mean: (values, period) => calculateStepsMean(values, period)
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A contagem diária de passos reflete o nível de atividade física, promovendo saúde cardiovascular, controle de peso e bem-estar mental. Monitorá-la ajuda a incentivar hábitos mais ativos e alinhados aos objetivos de saúde.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'bar',
      formatValue: value => decimalToString(Math.floor(Number(value)))
    },
    manualInputGuide: {
      mask: ['000000'],
      placeholder: 'ex: 500',
      validate(value) {
        const steps = Number(value)

        if (Number.isNaN(steps) || steps < 0 || steps > 150000) {
          return 'Valor máximo permitido: 150000'
        }
        return false
      },
      getJson(value) {
        const steps = Number(value)
        return {
          steps
        }
      }
    }
  },
  temperature_celsius: {
    title: 'Temperatura Corporal',
    dataOptions: {
      type: 'ºC',
      formatValue: value =>
        value ? decimalToString(Number(value), undefined, 1) : value
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A temperatura corporal é um indicador essencial de saúde, ajudando a identificar infecções e avaliar tratamentos. Seu monitoramento regular permite detectar problemas precocemente e manter a estabilidade do organismo. Caso o paciente esteja monitorando a temperatura da pele e a frequência cardíaca é possível estimar a temperatura corporal com base em estudos científico citados nas referências.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      formatValue: value =>
        value ? decimalToString(Number(value), undefined, 1) : value,
      floatingAvg: true
    },
    manualInputGuide: {
      mask: ['00,0'],
      placeholder: 'ex: 36,6',
      validate(value) {
        const temperature = Number(value.replaceAll(',', '.'))

        if (Number.isNaN(temperature) || temperature < 32 || temperature > 45) {
          return 'Valores permitidos: entre 32,0 e 45,0'
        }
        return false
      },
      getJson(value) {
        const temperature_celsius = value.replaceAll(',', '.')
        return {
          temperature_celsius
        }
      }
    },
    references: [
      {
        title:
          'Estimating Core Body Temperature Under Extreme Environments Using Kalman Filtering',
        link: 'https://www.researchgate.net/publication/357559117_Estimating_Core_Body_Temperature_Under_Extreme_Environments_Using_Kalman_Filtering'
      },
      {
        title: 'Human Cardiovascular Responses to Passive Heat Stress',
        link: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4950975/'
      },
      {
        title: 'Estimating Resting Core Temperature Using Heart Rate',
        link: 'https://journals.humankinetics.com/view/journals/jmpb/1/2/article-p79.xml'
      }
    ]
  },
  skin_temperature_celsius: {
    title: 'Temperatura da Pele',
    dataOptions: {
      type: 'ºC',
      formatValue: value =>
        value ? decimalToString(Number(value), undefined, 1) : value
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A temperatura da pele é um indicador essencial de saúde, ajudando a identificar infecções e avaliar tratamentos. Seu monitoramento regular permite detectar problemas precocemente e manter a estabilidade do organismo.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar',
      formatValue: value =>
        value ? decimalToString(Number(value), undefined, 1) : value,
      floatingAvg: true
    },
    manualInputGuide: {
      mask: ['00,0'],
      placeholder: 'ex: 32,6',
      validate(value) {
        const temperature = Number(value.replaceAll(',', '.'))

        if (Number.isNaN(temperature) || temperature < 10 || temperature > 50) {
          return 'Valores permitidos: entre 10,0 e 50,0'
        }
        return false
      },
      getJson(value) {
        const skin_temperature_celsius = value.replaceAll(',', '.')
        return {
          skin_temperature_celsius
        }
      }
    }
  },
  blood_pressure_systolic: {
    title: 'Pressão Arterial',
    dataOptions: {
      type: 'mmHg',
      showAllInSummary: true,
      separator: '/',
      auxData: ['blood_pressure_diastolic']
    },
    info: [
      {
        title: 'Descrição',
        description:
          'A pressão arterial é um indicador crucial da saúde cardiovascular, ajudando a detectar hipertensão, hipotensão e outras condições cardiovasculares. Seu monitoramento regular avalia riscos cardíacos, eficácia de tratamentos e impacto do estilo de vida.'
      },
      {
        title: 'Aviso Importante',
        description:
          'Métricas isoladas são úteis, mas precisam ser analisadas em conjunto por profissionais da saúde para avaliações e tratamentos precisos.'
      }
    ],
    graphOptions: {
      type: 'lines-with-bar'
    },
    manualInputGuide: {
      mask: ['000/00', '00/00', '00/000', '000/000'],
      placeholder: 'ex: 120/80',
      validate(value) {
        const bloodPressure = value.split('/')

        const [systolic, diastolic] = [
          Number(bloodPressure[0]),
          Number(bloodPressure[1])
        ]

        if (
          Number.isNaN(systolic) ||
          systolic < 50 ||
          systolic > 250 ||
          Number.isNaN(diastolic) ||
          diastolic < 30 ||
          diastolic > 150
        ) {
          return 'Valores permitidos: 50 a 250 / 30 a 150'
        }
        return false
      },
      getJson(value) {
        const [blood_pressure_systolic, blood_pressure_diastolic] =
          value.split('/')
        return {
          blood_pressure_systolic,
          blood_pressure_diastolic
        }
      }
    }
  },
  blood_pressure_diastolic: {
    title: 'Diastólica',
    info: [],
    graphOptions: {
      type: 'lines-with-bar'
    }
  }
}
